import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from 'react-toastify';
import request from '../../../utils/Request.utils';
import Modal from '../../component/Modal';

const FAQ_CATEGORY_CODE_GROUP = "board_title_ty";
const getCategoryName = (category) => category.code_nm_kr || category.code_nm || "";
const getCategoryLabel = (category) => {
  const categoryName = getCategoryName(category);
  return /^\d+$/.test(categoryName) ? `분류 ${categoryName}` : categoryName;
};

const FaqCategory = () => {
  const [categoryList, setCategoryList] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editValues, setEditValues] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(() => {
    setLoading(true);
    request.get("/common/code/board_title_ty", {}, (response) => {
      if (response.messageCode === 200) {
        const categories = response.data?.data_list || [];
        setCategoryList(categories);
        setSelectedCategories((prevSelectedCategories) =>
          prevSelectedCategories.filter((commCode) =>
            categories.some((category) => category.comm_code === commCode)
          )
        );
        setEditValues(categories.reduce((acc, category) => {
          acc[category.comm_code] = getCategoryLabel(category);
          return acc;
        }, {}));
      } else {
        toast.error("FAQ 분류 정보를 불러올 수 없습니다.");
      }
      setLoading(false);
    }, () => {
      toast.error("FAQ 분류 정보를 불러올 수 없습니다.");
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAddCategory = async () => {
    const categoryName = newCategoryName.trim();

    if (!categoryName) {
      toast.error("추가할 분류명을 입력해 주세요.");
      return;
    }

    await request.put("/common/code", {
      code_gr: FAQ_CATEGORY_CODE_GROUP,
      code_nm_kr: categoryName,
    }, (response) => {
      if (response.messageCode === 200) {
        toast.success("FAQ 분류가 추가되었습니다.");
        setNewCategoryName("");
        fetchCategories();
      } else {
        toast.error("FAQ 분류 추가 중 오류가 발생했습니다.");
      }
    }, () => {
      toast.error("FAQ 분류 추가 중 오류가 발생했습니다.");
    });
  };

  const handleUpdateCategory = async (commCode) => {
    const categoryName = (editValues[commCode] || "").trim();

    if (!categoryName) {
      toast.error("분류명을 입력해 주세요.");
      return;
    }

    await request.put(`/common/code/${commCode}`, {
      code_nm_kr: categoryName,
    }, (response) => {
      if (response.messageCode === 200) {
        toast.success("FAQ 분류가 수정되었습니다.");
        fetchCategories();
      } else if (response.messageCode === 411) {
        toast.error("수정된 내용이 없습니다.");
      } else {
        toast.error("FAQ 분류 수정 중 오류가 발생했습니다.");
      }
    }, () => {
      toast.error("FAQ 분류 수정 중 오류가 발생했습니다.");
    });
  };

  const deleteCategories = async (commCodes) => {
    await request.del("/common/code/delete", {
      comm_codes: commCodes,
    }, (response) => {
      if (response.messageCode === 200) {
        toast.success("FAQ 분류가 삭제되었습니다.");
        setSelectedCategories([]);
        fetchCategories();
      } else {
        toast.error("FAQ 분류 삭제 중 오류가 발생했습니다.");
      }
    }, () => {
      toast.error("FAQ 분류 삭제 중 오류가 발생했습니다.");
    });
  };

  const handleSelectCategory = (commCode) => {
    setSelectedCategories((prevSelectedCategories) =>
      prevSelectedCategories.includes(commCode)
        ? prevSelectedCategories.filter((selectedCode) => selectedCode !== commCode)
        : [...prevSelectedCategories, commCode]
    );
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === categoryList.length) {
      setSelectedCategories([]);
      return;
    }

    setSelectedCategories(categoryList.map((category) => category.comm_code));
  };

  const openDeleteSelectedModal = () => {
    if (selectedCategories.length === 0) {
      toast.error("삭제할 FAQ 분류를 선택해 주세요.");
      return;
    }

    setModalIsOpen(true);
  };

  const closeModal = () => setModalIsOpen(false);

  const handleDeleteSelectedCategories = async () => {
    try {
      await deleteCategories(selectedCategories);
    } finally {
      closeModal();
    }
  };

  const handleDeleteCategory = async (commCode) => {
    if (!window.confirm("선택한 FAQ 분류를 삭제하시겠습니까?")) return;

    await deleteCategories([commCode]);
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">FAQ 분류 관리</h2>
      <div className="adm_com_pg_content">
        <div className="adm_com_board_list__top">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: "10px" }}>
            <button className={`adm_com_btn ${selectedCategories.length > 0 ? "blue" : "gray"}`} onClick={openDeleteSelectedModal} disabled={selectedCategories.length === 0}>선택삭제</button>
            <div className="adm_com_board_search" style={{ marginLeft: "auto" }}>
              <article className="search end" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="추가할 분류명을 입력해 주세요."
                  className="input ws type_search"
                />
                <button className="adm_com_btn blue" onClick={handleAddCategory}>추가</button>
              </article>
            </div>
          </div>
        </div>

        {loading ? null : (
          <table className="adm_com_board_list">
            <colgroup>
              <col width="5%" />
              <col width="8%" />
              <col width="20%" />
              <col />
              <col width="15%" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="check">
                    <input
                      type="checkbox"
                      id="faqCategoryChkAll"
                      className="adm_com_chk"
                      onChange={handleSelectAll}
                      checked={categoryList.length > 0 && selectedCategories.length === categoryList.length}
                    />
                    <label htmlFor="faqCategoryChkAll" />
                  </div>
                </th>
                <th>번호</th>
                <th>분류 코드</th>
                <th>분류명</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {categoryList.map((category, index) => (
                <tr key={category.comm_code}>
                  <td>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={`faqCategoryChk${category.comm_code}`}
                        className="adm_com_chk"
                        checked={selectedCategories.includes(category.comm_code)}
                        onChange={() => handleSelectCategory(category.comm_code)}
                      />
                      <label htmlFor={`faqCategoryChk${category.comm_code}`} />
                    </div>
                  </td>
                  <td>{categoryList.length - index}</td>
                  <td>{category.comm_code}</td>
                  <td>
                    <input
                      type="text"
                      className="input"
                      value={editValues[category.comm_code] || ""}
                      onChange={(e) => setEditValues((prev) => ({
                        ...prev,
                        [category.comm_code]: e.target.value,
                      }))}
                    />
                  </td>
                  <td>
                    <button className="adm_com_btn blue line" onClick={() => handleUpdateCategory(category.comm_code)}>수정</button>
                    <button className="adm_com_btn gray line" onClick={() => handleDeleteCategory(category.comm_code)}>삭제</button>
                  </td>
                </tr>
              ))}
              {categoryList.length === 0 && (
                <tr>
                  <td colSpan={5}>등록된 FAQ 분류가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <Modal
        open={modalIsOpen}
        close={closeModal}
        type="confirm"
        popSize="s"
        title="삭제 확인"
        description={`선택된 FAQ 분류 ${selectedCategories.length}개를 삭제하시겠습니까?`}
      >
        <div className="btn_container">
          <button className="adm_com_btn blue" onClick={handleDeleteSelectedCategories}>삭제</button>
          <button className="adm_com_btn gray" onClick={closeModal}>취소</button>
        </div>
      </Modal>
      <ToastContainer />
    </main>
  );
};

export default FaqCategory;
